import React, { useCallback, useRef, useState } from 'react';

import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
  TypographyStylesProvider,
  useMantineTheme,
} from '@mantine/core';
import { useShallowEffect } from '@mantine/hooks';
import { IconPrinter } from '@tabler/icons-react';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import dayjs from 'dayjs';
import useDocument from 'hooks/document/useDocument';
import useSendDocumentEmail from 'hooks/document/useSendDocumentEmail';
import useSignDocument from 'hooks/document/useSignDocument';
import { useTracking } from 'hooks/mixpanel/useTracking';
import SignatureCanvas from 'react-signature-canvas';
import notificationHandler from 'utils/notifications';
import { Image } from 'utils/tip-tap-image-extension';

const DocumentSignModal = ({
  opened,
  documentId,
  closeHandler,
  permission = true,
}: {
  opened: boolean;
  documentId: string;
  closeHandler: () => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { successNotification, errorNotification } = notificationHandler();

  const sigRef = useRef<SignatureCanvas>(null);

  const [title, setTitle] = useState('');
  const [isSigned, setIsSigned] = useState(false);
  const [contentsWithCheckboxes, setContentsWithCheckboxes] = useState<JSX.Element>(<></>);
  const [numberOfCheckBoxes, setNumberOfCheckBoxes] = useState(0);
  const [numberOfCheckedCheckBoxes, setNumberOfCheckedCheckBoxes] = useState(0);

  const { data, isSuccess, isLoading } = useDocument(documentId);

  const sendDocumentEmailMutation = useSendDocumentEmail();
  const signMutation = useSignDocument();
  const tracking = useTracking();

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline,
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'document-gym-logo',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: data?.data?.contents || '',
    editable: false,
  });

  const downloadPdf = (): void => {
    if (data?.data && isSuccess) {
      const link = document.createElement('a');
      link.href = data.data.pdf || '';
      link.click();
    }
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    setIsSigned(false);
    editor?.commands.setContent('');
    sigRef.current?.clear();
    setNumberOfCheckedCheckBoxes(0);
  };

  const handleClearSignature = (): void => {
    sigRef.current?.clear();
    setIsSigned(false);
  };

  const handleExecute = (): void => {
    if (sigRef.current && data?.data && isSuccess) {
      const signDocument = {
        documentId: documentId,
        signature: sigRef.current?.toDataURL(),
        signedDate: dayjs(new Date()).format('YYYY-MM-DDTHH:mm:ss'),
      };
      signMutation.mutate(signDocument, {
        onSuccess: response => {
          successNotification(
            'Document signed successfully.',
            `${response.data.description} successfully signed.`,
          );

          tracking.trackDocumentSign(
            signDocument.documentId,
            response.data.description || data.data.description,
          );

          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Document sign failed.', error.response?.data.message);
        },
      });
    }
  };

  const handleAgreementCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      setNumberOfCheckedCheckBoxes(prev => prev + 1);
    } else {
      setNumberOfCheckedCheckBoxes(prev => prev - 1);
    }
  }, []);

  const returnContentsWithAgreementCheckboxes = useCallback(
    (paragraphs: string[]): JSX.Element => {
      return (
        <>
          {paragraphs.map((paragraph, index) => (
            <React.Fragment key={index}>
              <TypographyStylesProvider>
                <div dangerouslySetInnerHTML={{ __html: paragraph }} />
              </TypographyStylesProvider>
              {index !== paragraphs.length - 1 && (
                <Checkbox
                  key={`checkbox-${index}`}
                  required
                  label='I have read and accept the text above.'
                  name={`agreement-${index}`}
                  className='agreement-checkbox'
                  onChange={handleAgreementCheckboxChange}
                />
              )}
            </React.Fragment>
          ))}
        </>
      );
    },
    [handleAgreementCheckboxChange],
  );

  const handleSendEmail = useCallback((): void => {
    if (data?.data) {
      sendDocumentEmailMutation.mutate(data?.data, {
        onError: error => {
          errorNotification('Document email failed to send.', error?.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Email sent successfully', 'Document email sent .');
        },
      });
    }
  }, [sendDocumentEmailMutation, data?.data, errorNotification, successNotification]);

  useShallowEffect(() => {
    if (data?.data && isSuccess) {
      setTitle(data.data.description);
      editor?.commands.setContent(data.data.contents);
      if (data.data.signedDate && sigRef.current) {
        sigRef.current.fromDataURL(data.data.signature as string);
        setIsSigned(false);
        sigRef.current?.off();
      }
      var paragraphs = data.data.contents.split('REQUEST_ATHLETE_INITIALS');
      setNumberOfCheckBoxes(paragraphs.length - 1);
      setNumberOfCheckedCheckBoxes(0);
      setContentsWithCheckboxes(returnContentsWithAgreementCheckboxes(paragraphs));
    }
  }, [data?.data, sigRef.current, returnContentsWithAgreementCheckboxes]);

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      centered
      title={
        <Group spacing='xl'>
          <Text color='dimmed' fs='italic' fz='lg' fw='bold'>
            {data?.data ? `${data.data.type.charAt(0).toUpperCase()}${data.data.type.slice(1)}` : ''}
          </Text>
          <Title order={3}>{title}</Title>
        </Group>
      }
      size='auto'
    >
      <LoadingOverlay visible={isLoading} />
      <Stack spacing='xl'>
        <Paper p='sm' shadow='sm' radius='lg'>
          {contentsWithCheckboxes}
        </Paper>
        {permission && (
          <Stack>
            <Group position='center' px='xl'>
              <Text fs='italic' color='dimmed'>
                Signature
              </Text>
              <SignatureCanvas
                data-cy='signatureInput'
                canvasProps={{ height: 100, width: 400 }}
                backgroundColor={theme.colors.gray[2]}
                ref={sigRef}
                onEnd={() => setIsSigned(true)}
              />
            </Group>
            {data?.data.signedDate ? (
              <Group position='right' noWrap>
                <Button
                  onClick={handleSendEmail}
                  loading={sendDocumentEmailMutation.isLoading}
                  disabled={!permission}
                >
                  Email Copy to Athlete
                </Button>
                <Tooltip label='Download PDF'>
                  <ActionIcon>
                    <IconPrinter onClick={downloadPdf} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ) : (
              <Group position='right' noWrap>
                <Button
                  disabled={!isSigned || signMutation.isLoading}
                  onClick={handleClearSignature}
                  color='red'
                >
                  Clear
                </Button>
                <Button
                  disabled={!isSigned || numberOfCheckedCheckBoxes !== numberOfCheckBoxes}
                  loading={signMutation.isLoading}
                  onClick={handleExecute}
                  data-cy='clearSignButton'
                >
                  Execute
                </Button>
                <Button
                  disabled={isSigned}
                  onClick={handleSendEmail}
                  loading={sendDocumentEmailMutation.isLoading}
                >
                  Email to Athlete
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default DocumentSignModal;
